import type { SunoPrompt, SunoPromptGroup } from '../../../api/suno'
import { DeleteConfirmDialog } from '../../../components/DeleteConfirmDialog'
import { adminMediaPlayerClassName } from '../../../components/ui'
import { AdminAudioPlayer } from '../../audio/AdminAudioPlayer'
import type { PlayableTrack } from '../../audio/useAudioPlayer'
import { PromptEditorDialog, type PromptEditorState } from '../PromptEditorDialog'
import { PromptPoolDialog, type PromptPoolEditorState } from './PromptManagement'
import {
  SunoPromptSimilarityReview,
  SunoPromptTracksDrawer
} from './PromptManagementDrawers'

type PromptLibraryOverlaysProps = {
  audioCurrentTime: number
  audioDuration: number
  audioLoading: boolean
  audioMuted: boolean
  audioPlaying: boolean
  audioVolume: number
  groups: SunoPromptGroup[]
  loadingPromptId: string | null
  onChanged: () => void
  onClosePromptDelete: () => void
  onClosePromptEditor: () => void
  onClosePromptSimilarity: () => void
  onClosePromptTracks: () => void
  onClosePromptPoolEditor: () => void
  onConfirmDeletePrompt: (prompt: SunoPrompt) => Promise<boolean>
  onOpenPool: (poolId: string, anchorPromptId: string) => void
  onPlayPromptTracks: (prompt: SunoPrompt) => Promise<void>
  onPromptEditorChange: (value: PromptEditorState) => void
  onPromptPoolEditorChange: (value: PromptPoolEditorState) => void
  onPromptPoolSave: (value: PromptPoolEditorState) => void
  onPromptSave: (value: PromptEditorState) => Promise<boolean>
  onSeek: (time: number) => void
  onStopPlayback: () => void
  onSuggestPromptMetadata: (input: {
    excludePrompt: string
    stylePrompt: string
  }) => Promise<{ title: string; description: string }>
  onToggleMute: () => void
  onTogglePlayback: () => void
  onVolumeChange: (volume: number) => void
  playbackPromptId: string | null
  playbackTracks: PlayableTrack[]
  playingTrack: PlayableTrack | null
  promptDeleting: boolean
  promptEditor: PromptEditorState | null
  promptPoolEditor: PromptPoolEditorState | null
  promptPoolSaving: boolean
  promptSaving: boolean
  promptSimilarityPrompt: SunoPrompt | null
  promptToDelete: SunoPrompt | null
  promptTracksDrawerPrompt: SunoPrompt | null
  onNextTrack?: (() => void) | undefined
}

const pc = adminMediaPlayerClassName

export const PromptLibraryOverlays = ({
  audioCurrentTime,
  audioDuration,
  audioLoading,
  audioMuted,
  audioPlaying,
  audioVolume,
  groups,
  loadingPromptId,
  onChanged,
  onClosePromptDelete,
  onClosePromptEditor,
  onClosePromptPoolEditor,
  onClosePromptSimilarity,
  onClosePromptTracks,
  onConfirmDeletePrompt,
  onNextTrack,
  onOpenPool,
  onPlayPromptTracks,
  onPromptEditorChange,
  onPromptPoolEditorChange,
  onPromptPoolSave,
  onPromptSave,
  onSeek,
  onStopPlayback,
  onSuggestPromptMetadata,
  onToggleMute,
  onTogglePlayback,
  onVolumeChange,
  playbackPromptId,
  playbackTracks,
  playingTrack,
  promptDeleting,
  promptEditor,
  promptPoolEditor,
  promptPoolSaving,
  promptSaving,
  promptSimilarityPrompt,
  promptToDelete,
  promptTracksDrawerPrompt
}: PromptLibraryOverlaysProps) => (
  <>
    {promptEditor && (
      <PromptEditorDialog
        groups={groups}
        isSaving={promptSaving}
        onCancel={onClosePromptEditor}
        onChange={onPromptEditorChange}
        onGenerateMetadata={onSuggestPromptMetadata}
        onSave={(input) => {
          void onPromptSave(input).then((saved) => {
            if (saved) {
              onClosePromptEditor()
            }
          })
        }}
        value={promptEditor}
      />
    )}

    {promptPoolEditor && (
      <PromptPoolDialog
        isSaving={promptPoolSaving}
        onCancel={onClosePromptPoolEditor}
        onChange={onPromptPoolEditorChange}
        onSave={onPromptPoolSave}
        value={promptPoolEditor}
      />
    )}

    {promptToDelete && (
      <DeleteConfirmDialog
        description="This saved Suno prompt will be permanently deleted from the library. If it belongs to a pool, it will be removed from that pool first."
        isDeleting={promptDeleting}
        itemName={promptToDelete.title}
        onCancel={onClosePromptDelete}
        onConfirm={() => {
          void onConfirmDeletePrompt(promptToDelete).then((deleted) => {
            if (deleted) {
              onClosePromptDelete()
            }
          })
        }}
        title="Delete prompt?"
      />
    )}

    {promptTracksDrawerPrompt && (
      <SunoPromptTracksDrawer
        onChanged={onChanged}
        onClose={onClosePromptTracks}
        prompt={promptTracksDrawerPrompt}
      />
    )}

    {promptSimilarityPrompt && (
      <SunoPromptSimilarityReview
        audioCurrentTime={audioCurrentTime}
        audioDuration={audioDuration}
        audioLoading={audioLoading}
        audioMuted={audioMuted}
        audioPlaying={audioPlaying}
        audioVolume={audioVolume}
        loadingPromptId={loadingPromptId}
        onChanged={onChanged}
        onClose={onClosePromptSimilarity}
        onOpenPool={(poolId) => {
          const anchorPromptId = promptSimilarityPrompt.id
          onClosePromptSimilarity()
          onOpenPool(poolId, anchorPromptId)
        }}
        onPlayPromptTracks={onPlayPromptTracks}
        onSeek={onSeek}
        onStopPlayback={onStopPlayback}
        onToggleMute={onToggleMute}
        onTogglePlayback={onTogglePlayback}
        onVolumeChange={onVolumeChange}
        playbackPromptId={playbackPromptId}
        playbackTracks={playbackTracks}
        playingTrack={playingTrack}
        prompt={promptSimilarityPrompt}
        {...(onNextTrack ? { onNextTrack } : {})}
      />
    )}

    {!promptSimilarityPrompt && (
      <AdminAudioPlayer
        currentTime={audioCurrentTime}
        duration={audioDuration}
        isPlaying={audioPlaying}
        loading={audioLoading}
        muted={audioMuted}
        track={playingTrack}
        volume={audioVolume}
        onSeek={onSeek}
        onTogglePlayback={onTogglePlayback}
        onToggleMute={onToggleMute}
        onVolumeChange={onVolumeChange}
        onStop={onStopPlayback}
        {...(onNextTrack ? { onNext: onNextTrack } : {})}
      />
    )}
    {playingTrack && !promptSimilarityPrompt && <div className={pc('admin-audio-player-spacer')} />}
  </>
)
